import { useCallback, useMemo } from "react";
import { WizardState, WizardStepId } from "../types";
import { COOLDOWN_MS } from "../constants";

const ONBOARDING_COMPLETED_IDS_KEY = "easyfit_onboarding_completed_user_ids";

interface UseWizardStorageProps {
  userId: number | undefined;
}

export const useWizardStorage = ({ userId }: UseWizardStorageProps) => {
  const storageKey = useMemo(
    () => (userId ? `easyfit_wizard_chat_${userId}` : null),
    [userId],
  );

  const cooldownKey = useMemo(
    () => (userId ? `easyfit_wizard_cooldown_${userId}` : null),
    [userId],
  );

  const hasCompletedOnboarding = useCallback((id: number) => {
    try {
      const raw = localStorage.getItem(ONBOARDING_COMPLETED_IDS_KEY);
      if (!raw) return false;
      const ids = JSON.parse(raw) as number[];
      return Array.isArray(ids) && ids.includes(id);
    } catch {
      return false;
    }
  }, []);

  const markOnboardingCompleted = useCallback((id: number) => {
    try {
      const raw = localStorage.getItem(ONBOARDING_COMPLETED_IDS_KEY);
      const ids: number[] = raw ? (JSON.parse(raw) as number[]) : [];
      if (!Array.isArray(ids)) return;
      if (!ids.includes(id)) {
        ids.push(id);
        localStorage.setItem(ONBOARDING_COMPLETED_IDS_KEY, JSON.stringify(ids));
      }
    } catch {
      localStorage.setItem(ONBOARDING_COMPLETED_IDS_KEY, JSON.stringify([id]));
    }
  }, []);

  const canShowWizard = useCallback(() => {
    if (!cooldownKey) return true;

    const lastClosedStr = localStorage.getItem(cooldownKey);
    if (!lastClosedStr) return true;

    const lastClosed = Number.parseInt(lastClosedStr, 10);
    const now = Date.now();

    return now - lastClosed >= COOLDOWN_MS;
  }, [cooldownKey]);

  const saveCooldown = useCallback(() => {
    if (!cooldownKey) return;
    localStorage.setItem(cooldownKey, Date.now().toString());
  }, [cooldownKey]);

  const loadState = useCallback((): WizardState | null => {
    if (!storageKey) return null;

    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as WizardState;
    } catch {
      localStorage.removeItem(storageKey);
      return null;
    }
  }, [storageKey]);

  const persistState = useCallback(
    (stepId: WizardStepId, completedSteps: WizardStepId[]) => {
      if (!storageKey) return;

      const state: WizardState = {
        currentStepId: stepId,
        completedSteps,
      };

      localStorage.setItem(storageKey, JSON.stringify(state));
    },
    [storageKey],
  );

  const clearState = useCallback(() => {
    if (!storageKey) return;
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  return {
    canShowWizard,
    saveCooldown,
    loadState,
    persistState,
    clearState,
    hasCompletedOnboarding,
    markOnboardingCompleted,
  };
};
